/**
 * Configuration and the BlazePose 33-landmark topology.
 *
 * The landmark names and their ordering are defined by MediaPipe's pose
 * landmarker model; see
 * https://ai.google.dev/edge/mediapipe/solutions/vision/pose_landmarker
 */

export const DEFAULT_LINE_WIDTH = 2;
export const DEFAULT_RADIUS = 4;

export const VIDEO_SIZE = {
  "1280 X 720": { width: 1280, height: 720 },
  "640 X 480": { width: 640, height: 480 },
  "640 X 360": { width: 640, height: 360 },
  "360 X 270": { width: 360, height: 270 },
};

/**
 * MediaPipe fetches its Wasm runtime at load time rather than through the
 * bundler, so it is served from this app's own origin — see
 * scripts/copy-wasm.mjs, which stages it into dist/. Serving it ourselves keeps
 * the runtime locked to the installed @mediapipe/tasks-vision version and means
 * the app needs no third-party CDN to start.
 */
export const WASM_PATH = "/wasm";

/**
 * Model variants, staged into dist/models by scripts/fetch-assets.mjs and
 * served from this origin. Ordered least to most accurate: `heavy` is the one
 * for offline form analysis (and needs `yarn fetch-assets --all`), `full` is
 * the sensible live default, `lite` is for low-end mobile and is visibly
 * noisier on fast limbs.
 */
export const MODEL_ASSETS = {
  lite: "/models/pose_landmarker_lite.task",
  full: "/models/pose_landmarker_full.task",
  heavy: "/models/pose_landmarker_heavy.task",
};

export const STATE = {
  camera: {
    targetFPS: 60,
    sizeOption: "640 X 480",
    /**
     * On a phone the camera is asked for 360x270 regardless of the option
     * above, which is what the vendored demo did and what every recording so
     * far was captured at. It is cheap per frame, and for an angle at the hip
     * or the knee it is plenty. It is NOT plenty for the head: the face
     * landmarks the head angle is built from are a handful of pixels across at
     * that size. `?camsize=` overrides it, so the cost in frame rate can be
     * measured on the phone it would run on rather than guessed at here.
     */
    mobileSizeOption: "360 X 270",
  },
  /**
   * "camera" for the live view, "file" to analyse a recording instead. Reviewing
   * a file is easier than reading numbers off a live 60fps overlay, and it is
   * the only way to run this pipeline where there is no camera.
   */
  sourceKind: "camera",
  model: "full",
  landmarker: {
    numPoses: 1,
    minPoseDetectionConfidence: 0.5,
    minPosePresenceConfidence: 0.5,
    minTrackingConfidence: 0.5,
    outputSegmentationMasks: false,
  },
  /** Landmarks below this visibility are not drawn. */
  visibilityThreshold: 0.5,
  render3D: false,
  /**
   * Metrics are computed for one declared camera view at a time. Each metric
   * names the views it is valid in (see metrics/definitions.js); the view set
   * here decides which of them are computed and shown.
   */
  metrics: {
    enabled: true,
    view: "side",
    /** Armed by ?record=1: starts recording at the first detected pose. */
    autoRecord: false,
    /**
     * ?capture=1 shows the recording bar instead of the drill bar. Capturing a
     * fixture is an errand for whoever is tuning the detector; the drill bar is
     * what the athlete came for.
     */
    capture: false,
    /**
     * Whether to show the landing screen before the camera is asked for.
     * `null` means nobody said, and `showIntro()` in index.js decides from the
     * rest of the flow; ?intro= sets it either way.
     */
    intro: null,
    /**
     * Tones on each rep and through the countdown. ?sound=0 silences them.
     * On by default: filmed side-on the athlete is at ninety degrees to the
     * phone, so a sound is the only feedback that does not cost them a turn of
     * the head mid-rep.
     */
    sound: true,
    /**
     * ?soundcheck=1 turns Start into a speaker test that plays the good and
     * fault sounds back to back. A diagnostic, not part of the drill: the only
     * other way to hear the fault sound is to perform a bad rep on purpose.
     */
    soundcheck: false,
    /**
     * Seconds of countdown before recording actually starts, so the phone can
     * be set down and the athlete can walk into frame. 0 starts immediately.
     */
    countdownSeconds: 5,
    /** Rolling span the metrics are computed over, in milliseconds. */
    windowMs: 3000,
    /** Frames retained. At 60fps this is ten seconds, comfortably over windowMs. */
    bufferFrames: 600,
    /**
     * Which drill to score, by its id in exercises/index.js. Null shows the
     * exercise menu first, which is the normal way in; `?exercise=arm_swing`
     * goes straight to that drill, which is how a recording is reviewed and how
     * the pipeline is driven headlessly.
     */
    exercise: null,
  },
  /**
   * Thresholds for the posture criteria scored on each rep. See
   * docs/arm-swing-posture.md: every number here is measured off one athlete's
   * ten reps read by one person, not a coaching standard, which is exactly why
   * they are settings and are overridable by URL parameter.
   */
  posture: {
    enabled: true,
    /** Heel lift that counts as on the toes, in foot lengths. Flat is ~0.3. */
    tiptoeMin: 0.7,
    /** Knee must bend at least to this angle. Measured reps reach ~104. */
    kneeFlexMax: 150,
    /** Trunk tilt range allowed per rep. The stand-in for core engagement. */
    trunkRockMax: 20,
    /** How far ahead of the knee the head or chest may sit, in centimetres. */
    aheadOfKneeMax: 10,
    /**
     * The athlete's own neutral head pitch, in degrees from square to the
     * trunk. This is anatomy, not form: the ear-to-nose line is not exactly
     * perpendicular to the spine on anyone. The default is the figure measured
     * on the reference recording, so the criterion is usable out of the box;
     * a still capture with the neck deliberately neutral pins it per athlete.
     */
    neckOffset: 18,
    /** Allowed deviation from neckOffset. */
    neckTolerance: 15,
    /** Leg landmarks below this are the model guessing, so the frame is refused. */
    /**
     * How far the up/down speed ratio has to sit from 1.0 before the readout
     * calls either direction faster. Inside the band it says "Even".
     *
     * 0.10 because that is the measurement's own resolution: the ratio has a
     * standard deviation of about 0.09 between reps of the reference take, so a
     * smaller band would report a difference the method cannot see. The ratio
     * stopped being a pass/fail criterion on 2026-10-03 — it is reported and
     * never judged — so this only decides what the row says, never a verdict.
     */
    tempoEvenBand: 0.1,
    /**
     * Degrees per second over a whole rep, below which the swing is too slow.
     *
     * The one threshold here that is not a judgement call. On the reference take
     * the eight reps performed at pace run 186 to 225 degrees/second and the two
     * that exist only because the athlete was walking in or had paused come out
     * at 74 and 95, with nothing in between. Any value from 120 to 180 separates
     * them identically; 150 is the middle of that gap, 24% below the slowest real
     * rep and 58% above the fastest false one.
     */
    swingSpeedMin: 150,
    minVisibility: 0.5,
    /** A longer gap between frames breaks rep accumulation rather than bridging it. */
    maxGapMs: 250,
    /*
     * Squat thresholds. Unlike every number above, these are NOT measured: they
     * are the movement's own anatomy plus judgement, which is why the squat
     * jump declares `calibrated: false` and why the receipt prints that.
     * Calibrating them means the protocol in fixtures/README.md, the same one
     * the swing went through. The two COUNTING thresholds below are the
     * exception: those were measured, in docs/squat-jump.md.
     *
     * The two counting thresholds sit far apart on purpose. The knee angle
     * wanders a degree or two at the bottom of the movement — the noise floor
     * across this app is 1-3 degrees — and a counter watching one line would
     * score every wobble across it as another rep.
     */
    /**
     * Knee angle the counter-movement has to pass below for a rep to start.
     *
     * Shallow on purpose, and this is the subtle one. A jump taken with almost
     * no counter-movement is a fault the athlete should be told about, and at
     * 120 it was not a fault at all — it was a rep that never started, so it
     * vanished from the count instead of failing. Worse, it made
     * `jumpDepthMax` unreachable: a rep could only exist if the knee had
     * already passed 120, which is under the 125 the criterion asks for, so
     * depth passed by construction on every rep that could be scored. At 140
     * the shallow ones count and fail, which is what the athlete can act on.
     * Measured: the fault take goes from 9 reps to 17, five of them failing
     * depth at 126-139 degrees, and the good take still reads 11.
     */
    jumpKneeDown: 140,
    /**
     * And return above to close it. Deliberately NOT near a locked 180: on the
     * reference take the athlete came back to only 154-162 degrees between
     * jumps at a 1.5s cadence, so 160 merged five of the ten gaps and counted 6
     * reps against 11 performed. 155 already loses one. See docs/squat-jump.md.
     *
     * The 10 degrees between this and `jumpKneeDown` is the gap that stops the
     * wobble at either end counting twice, and it is as wide as the two
     * measurements allow: the line below cannot rise without missing a shallow
     * jump, and this one cannot without missing a real one.
     */
    jumpKneeUp: 150,
    /**
     * How long after `jumpKneeUp` the rep keeps collecting, so that the flight
     * is inside the window the criteria read. On the reference take the body
     * finished extending 350ms after the crossing and nothing changed past
     * 400ms. The cap is also what closes the last rep of a set, where the
     * athlete stands with the knee above `jumpKneeUp` and the signal never
     * comes back down. Milliseconds.
     */
    jumpFlightMs: 400,
    /** Deepest knee angle that counts as having loaded the jump. */
    jumpDepthMax: 125,
    /**
     * Knee angle the legs have to reach in the air. A straight leg reads near
     * 180; the reference take reached 154-167, so this passes it with little
     * room, which is deliberate — the drill is a stand-in for a hurdle jump and
     * straighter legs are the point.
     */
    jumpLegsMin: 150,
    /**
     * Hip angle — shoulder-hip-knee — the body line has to open to in the air.
     *
     * This is the one that measures "trunk and legs not extended", the fault
     * the athlete named first, and the knee does not: on his fault take the
     * knee reached 150-171, as straight as his good set, while the hip piked to
     * 141-155 against 163-178 clean. 158 keeps 5 degrees under the good set's
     * worst rep and still fails 10 of the 17 bad ones; a line at 160 would
     * catch 14 but sit 3 degrees off a correct rep, which is inside what this
     * measurement can resolve.
     */
    jumpHipsMin: 158,
    /**
     * Where the airborne phase of a jump starts and ends, as a share of foot
     * length of ankle extension. The criteria that judge the shape in the air
     * have to be read while the athlete IS in the air: the rep window runs past
     * the landing, and a knee read anywhere in it mostly reports someone
     * standing up. A flat foot measures around 0.3 because the heel landmark
     * sits above the toes anatomically, so 0.4 is the first value clear of one.
     *
     * Swept against both takes: at 0.5 the good set's weakest rep reads 151
     * against a 150 threshold, which is inside the noise floor. At 0.4 it reads
     * 154 and the deliberately-bent-leg take reads 125-171. See
     * docs/squat-jump.md.
     */
    jumpAirToes: 0.4,
    /**
     * And how much of it there has to be for the movement to be a jump at all.
     *
     * This is the one the athlete will want to move. The counter watches the
     * knee, and a knee bending on the floor traces the same curve as a knee
     * bending to jump, so settling between reps — stepping back into position,
     * shaking out a leg — was counted as an attempt and told it was a bad one.
     * Asking for flight is what separates the two, and it is what makes resting
     * mid-set free: a set is the reps performed in it, not the time between
     * them, and this drill is heavy enough that people stop in the middle.
     *
     * 100ms against the good take's shortest airborne phase of 266ms, so there
     * is 2.7x of margin on a real jump. It is also what the criteria need to
     * read the shape at all: two frames of air is not a body to judge.
     * Overridable as `?jumpairminms=`.
     */
    jumpAirMinMs: 100,
    /**
     * Trunk away from vertical at its most upright in the air. The reference
     * take reached 0.8-7.1 degrees, so this is clear of it by a wide margin and
     * is there to catch a jump taken folded rather than to grade a good one.
     */
    jumpTrunkMax: 15,
    /**
     * How far the arm has to swing BACK between the bottom of the load and the
     * air, in degrees. The arms go forward and down into the load and back and
     * up through the jump, which is the opposite way to the body and is the
     * athlete's own instruction.
     *
     * Measured: 145-172 degrees of travel across the good take's eleven reps,
     * against -99 to 144 on the take where the arms were left out of it. 110
     * sits under the good set's weakest rep by 7 degrees. Travel rather than a
     * finishing position, since where the arm ends up also depends on how the
     * athlete carries it at rest.
     */
    jumpArmSwingMin: 110,
    /*
     * V-sit thresholds, on the hip angle: shoulder-hip-knee, 180 lying flat and
     * closing as the body folds. **Measured**, off the take of 2026-10-13, in
     * which the athlete performed four labelled blocks of five reps in one
     * recording: clean, deliberately shallow, deliberately bent knees, and
     * lying leg raises with the back kept on the floor. Every number below was
     * moved by it. See docs/v-sit.md for the tables.
     */
    /** Hip angle the fold has to pass below for a rep to be under way. */
    vSitHipDown: 120,
    /**
     * And return above to close it. Coming back down is half the rep and this
     * is where that is enforced — a bounced rep that never unfolds is not
     * counted at all. **Measured, and lowered from 160**: with knees bent the
     * hip reads lower lying flat than it does with straight legs, so one rep of
     * the bent-knee block never returned above 160 and was swallowed into its
     * neighbour. 19 reps for 20 performed, which is the disappearing rep this
     * file predicted before the take existed. The take counts 20 of 20 at
     * anything from 145 to 155 and adds no rep at any of them.
     */
    vSitHipUp: 150,
    /**
     * Tightest hip angle that counts as a full fold. **Measured, from 95**: the
     * clean block folds to 61-65 and the deliberately shallow block to 96-117,
     * so 95 sat 1 degree off the fault and 30 off the clean. 85 is between them
     * with 20 degrees of margin on the clean side, where a false failure costs
     * more, and 11 on the fault side.
     */
    vSitFoldMax: 85,
    /**
     * Knee angle wanted while folded; below this the legs are tucked.
     * **Measured, from 150**: the bent-knee block reads 90-98 and the clean
     * block 149-170, the knee softening across the block as the athlete tires.
     * 150 failed the last clean rep by one degree, which is inside the noise
     * floor. 145 fails none of them and still catches every bent rep by 47.
     */
    vSitKneeMin: 145,
    /**
     * Degrees the trunk has to come off the floor within the rep, measured as
     * the rise in trunk elevation from the flattest frame of the rep to the
     * most upright one.
     *
     * **The hip angle alone cannot ask for this.** Shoulder-hip-knee closes
     * just as well when the legs come up to a trunk that never moved, so a lying
     * leg raise scored a clean V-sit — which is what the athlete reported.
     * Counting still runs off the hip, because that is what says an attempt
     * happened; this is the criterion that says it was the movement claimed.
     *
     * A rise rather than an absolute angle, because the floor is not a landmark:
     * what makes the number mean "off the floor" is that it is read against the
     * same rep's own flattest frame, so a phone propped at a tilt cannot shift
     * it.
     *
     * **Measured, from 20.** The leg raises read 1-3 degrees and every rep of
     * every other block reads 13 or more, so 20 was failing real reps: a shallow
     * fold and a tired bent-knee rep both raise the trunk less, and they were
     * being told to lift their chest when the fault was elsewhere. 8 sits
     * between the two populations with 5 degrees either side, and the clean
     * block is nowhere near it at 34-43.
     */
    vSitTrunkRise: 8,

    /**
     * Wall handstand. **Mostly still unmeasured.** One take exists
     * (`fixtures/wall_handstand/`) and it answered the question that mattered
     * most — MediaPipe does track an inverted body, cleanly — so these are no
     * longer angles written down against nothing. But one take of one athlete
     * calibrates one number: the head, which that take broke on purpose, and the
     * knee, which it showed failing a straight leg. Every other number here is
     * still a diver's line written down, and the drill still ships
     * `calibrated: false`. See `docs/wall-handstand.md`.
     */
    /** Trunk lean off vertical, in degrees, above which the body counts as
     *  inverted. 180 is fully upside down; this sits well clear of the kick-up
     *  so that going up and coming down are not part of the hold. */
    handstandInvertMin: 140,
    /** Shoulder-hip-ankle, the line through the whole body. 180 is a plank. */
    handstandLineMin: 165,
    /** Knee angle wanted in the hold. **Measured twice, in both directions.**
     *  170 was wrong and failed a straight leg every time, since a real straight
     *  leg upside down reads 166-170, so it went to 160 — which then passed
     *  three quarters of a knee bent on purpose. The third take settled it with
     *  the fault and the correction inside one hold: the bent stretch reads a
     *  median of 154 (min 130, p90 166) and the straight stretches of the same
     *  hold read 164-176 with a p10 of 166. 163 sits 3 degrees under that p10
     *  and 9 above the fault's median, fails no frame of a labelled clean
     *  stretch, and catches 86% of the fault against 76% at 160. The margin on
     *  the clean side is 1 degree against the single worst clean frame, which is
     *  inside the noise floor: this is the number to revisit first if a straight
     *  leg ever reads as bent. The kick-up frames read 45-124.
     *  See docs/wall-handstand.md. */
    handstandKneeMin: 163,
    /** Elbow angle wanted in the hold. */
    handstandElbowMin: 160,
    /** Wrist-shoulder-hip: the shoulder stacked over the hand. */
    handstandShoulderMin: 155,
    /* No toe threshold. The ankle angle does not track the foot: the athlete
     * flexed his feet deliberately and corrected them within one hold and the
     * best split anywhere in it separates the halves by 3.2 degrees, in the
     * wrong direction. The row reports and never judges, the way the squat
     * jump's row on the same joint does. See docs/wall-handstand.md. */
    /** Shorter than this upside down was a kick-up and a fall, not a hold. */
    handstandMinHoldMs: 2000,
    /** How long a change in the posture has to persist before it is announced.
     *  It is a sound in the athlete's ear, so a flicker at the threshold must
     *  not be one; see exercises/hold.js. */
    handstandSettleMs: 400,
    /** Metres of lean between hands and feet below which the wall is not
     *  called. A handstand leans the way the wall is, but only just. */
    handstandLeanMin: 0.04,
    /** Metres the hands may sit out from the wall. The feet are ON the wall, so
     *  the horizontal gap between the wrist and the ankle IS that distance. */
    handstandWallMax: 0.3,
    /** Degrees the ear may sit past the line of the arm, out behind it.
     *  **Measured**, and the one threshold on this drill with a fault take
     *  behind it: on the reference recording a clean hold reads -8 to -28 and
     *  never above +2, and the deliberately broken stretches read +10 to +47
     *  with a 10th percentile of +10. The margin is 8 degrees on the clean side
     *  of this and 2 on the fault side; see docs/wall-handstand.md. */
    handstandHeadMax: 8,
    /** And the other side of it. **Measured**, on the take that answered the
     *  question Alfre asked after watching the drill pass a head held much too
     *  far forward: the criterion was one-sided, so only the head thrown back
     *  could ever fail it. In the same hold he broke it both ways — forward
     *  reads -56 to -63, back reads +35 to +47 — with clean stretches either
     *  side of each reading -4 to -45. This bound sits 4.6 degrees under the
     *  worst clean frame of that take and 6.1 above the nearest fault frame.
     *  A head is out of line in two directions and the threshold has to be too;
     *  see docs/wall-handstand.md. */
    handstandHeadMin: -50,
    /** Metres of nose-to-ear offset below which the facing is not called, and
     *  the head criterion reports unjudged rather than guessing. The sign of
     *  the facing is what turns "one side of the arm" into "the chest side", so
     *  a facing read wrong inverts the criterion. On the reference take it never
     *  fell below 4.7cm across 2777 frames and never changed sign. */
    handstandFacingMin: 0.02,
  },
  smoothing: {
    enabled: true,
    /**
     * Tuned against a 60fps jitter/ramp sweep. beta is the lag/jitter dial:
     * raising it tracks fast motion more tightly at a small jitter cost.
     *   beta 0.0 -> lag 0.28   beta 1.0 -> lag 0.10
     *   beta 0.5 -> lag 0.15   beta 1.5 -> lag 0.08  (jitter is flat across all)
     * Sport motion wants low lag, so this sits high.
     */
    normalized: { minCutoff: 1.0, beta: 1.5, dCutoff: 1.0 },
    /** World landmarks are in metres, so speeds are ~3x larger; beta scales down. */
    world: { minCutoff: 1.0, beta: 0.5, dCutoff: 1.0 },
  },
};

/** The 33 BlazePose landmarks, in model output order. */
export const POSE_LANDMARK_NAMES = [
  "nose",
  "left_eye_inner",
  "left_eye",
  "left_eye_outer",
  "right_eye_inner",
  "right_eye",
  "right_eye_outer",
  "left_ear",
  "right_ear",
  "mouth_left",
  "mouth_right",
  "left_shoulder",
  "right_shoulder",
  "left_elbow",
  "right_elbow",
  "left_wrist",
  "right_wrist",
  "left_pinky",
  "right_pinky",
  "left_index",
  "right_index",
  "left_thumb",
  "right_thumb",
  "left_hip",
  "right_hip",
  "left_knee",
  "right_knee",
  "left_ankle",
  "right_ankle",
  "left_heel",
  "right_heel",
  "left_foot_index",
  "right_foot_index",
];

export const POSE_LANDMARKS = Object.fromEntries(
  POSE_LANDMARK_NAMES.map((name, i) => [name.toUpperCase(), i])
);

/**
 * Index sets by body side. Two naming conventions coexist in the model's own
 * landmark names — `left_shoulder` but `mouth_left` — and the odd/left,
 * even/right index parity only holds from the shoulders down, so match the
 * side as a word anywhere in the name rather than by prefix or parity.
 */
export const KEYPOINT_INDEX_BY_SIDE = {
  left: indicesOnSide("left"),
  right: indicesOnSide("right"),
  middle: POSE_LANDMARK_NAMES.map((_, i) => i).filter(
    (i) => side(POSE_LANDMARK_NAMES[i]) == null
  ),
};

function side(name) {
  const parts = name.split("_");
  if (parts.includes("left")) return "left";
  if (parts.includes("right")) return "right";
  return null;
}

function indicesOnSide(wanted) {
  return POSE_LANDMARK_NAMES.map((name, i) =>
    side(name) === wanted ? i : -1
  ).filter((i) => i >= 0);
}
